import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {MatSnackBar} from '@angular/material/snack-bar';
import {catchError, concatMap, delay, EMPTY, filter, map, of, switchMap, tap, withLatestFrom} from 'rxjs';
import {NGXLogger} from 'ngx-logger';
import {UserViewConfigActions} from '../action/user-view-config.actions';
import {MappedHttpErrorResponse, mappedHttpErrorResponseOperator} from '@adnova/jf-ng-components';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {AppState} from '../states/app.state';
import {UserViewConfigSelectors} from '../selector/user-view-config.selectors';
import {ViewConfigurationDTO, ViewConfigurationService} from '../../openapi/agrarinformationen-openapi';
import {CustomSnackbarComponent} from '../../modules/components/custom-snackbar/custom-snackbar.component';


@Injectable()
export class UserViewConfigEffects {
  private _logger = inject(NGXLogger);
  private _viewConfigService = inject(ViewConfigurationService);
  private _actions$ = inject(Actions);
  private _snackbar = inject(MatSnackBar);
  private _router = inject(Router);
  private _store = inject(Store<AppState>);


  readonly deleteConfig$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserViewConfigActions.deleteConfig),
      switchMap(() =>
        this._viewConfigService.deleteViewConfiguration().pipe(
          concatMap(() => {
            console.log('kf. deleteConfig succeeded – backend returned 204');
            setTimeout(() => {
              this._router.navigate(['/init-page']);
              setTimeout(() => {
                window.location.reload();
              }, 30);
            }, 2000);

            return [];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map((mappedError: MappedHttpErrorResponse) => {
              this._logger.error('deleteUserViewConfig failed.', mappedError);
              return {type: '[User View Config] noop'};
            })
          ))
        )
      )
    )
  );


  readonly navigateAfterViewConfigHandled$ = createEffect(
    () => this._actions$.pipe(
      ofType(UserViewConfigActions.navigateAfterViewConfigHandled),
      withLatestFrom(this._store.select(UserViewConfigSelectors.userViewConfigState)),
      map(([action, config]) => {
        // INFO: when user selects no options at the config page and saves the config, the app restarts.
        if (action.restartApp) {
          this._router.navigate(['/init-page']);
          return;
        }

        if (!config.meta.isValidConfigAvailable) {
          this._router.navigate(['/user-view-config']);
          return;
        }

        const hasNews = config.news?.show === true;
        const hasWeather = config.weather?.show === true;
        const hasCharts = config.agrarcharts?.show === true;

        if (!hasNews && !hasWeather && !hasCharts) {
          this._router.navigate(['/user-view-config']);
          return;
        }

        if (hasNews && (hasWeather || hasCharts)) {
          this._router.navigate(['/overview']);
        } else if (hasNews) {
          this._router.navigate(['/news']);
        } else if (hasWeather) {
          this._router.navigate(['/weather']);
        } else if (hasCharts) {
          this._router.navigate(['/agrarcharts']);
        } else {
          this._router.navigate(['/user-view-config']); // INFO: fallback
        }
      })
    ),
    {dispatch: false}
  );

  readonly readUserViewConfig$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserViewConfigActions.readUserViewConfig),
      switchMap(() =>
        this._viewConfigService.getViewConfiguration().pipe(
          concatMap((config: ViewConfigurationDTO) => {
            this._logger.debug('readUserViewConfig succeeded.');
            return [
              UserViewConfigActions.readUserViewConfigSuccess({config}),
              UserViewConfigActions.navigateAfterViewConfigHandled({})
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            concatMap((mappedError: MappedHttpErrorResponse) => {
              this._logger.error('readUserViewConfig failed.', 'error:', mappedError);
              return [
                // UserViewConfigActions.readUserViewConfigFailure({errorStatus: mappedError.status as number}),
                UserViewConfigActions.readUserViewConfigFailure({error: mappedError}),
              ];
            })
          ))
        )
      )
    )
  );

  readonly readUserViewConfigFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(UserViewConfigActions.readUserViewConfigFailure),
      tap(({error}) => {
        if(error.status === 404) return;

        let errorMsg = '';
        switch (error.status) {
          case 400:
            errorMsg = 'Die Anfrage war ungültig. Bitte überprüfe deine Eingaben oder versuche es später erneut.';
            break;
          case 403:
            errorMsg = 'Fehlende Berechtigung für die View-Konfiguration. Bitte kontaktiere deinen Administrator.';
            break;
          case 503:
            errorMsg = 'Der Service zur View-Konfiguration ist derzeit nicht verfügbar. Bitte versuche es später erneut.';
            break;
          default:
            errorMsg = 'Fehler beim Laden der View-Konfiguration. Bitte probiere es später erneut.';
        }

        this._snackbar.open(errorMsg, undefined, {
          duration: 5000,
          panelClass: 'error',
        });
      }),
      filter(({error}) => error.status === 404),
      map(() => UserViewConfigActions.navigateAfterViewConfigHandled({}))
    )
  );

  readonly createUserViewConfig$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserViewConfigActions.createUserViewConfig),
      switchMap(({config, restartAppAfterwards}) =>
        this._viewConfigService.createViewConfiguration(config).pipe(
          concatMap((created: ViewConfigurationDTO) => {
            this._logger.debug('createUserViewConfig succeeded.');

            this._snackbar.openFromComponent(CustomSnackbarComponent, {
              duration: 6000,
              panelClass: ['success'],
              data: {
                message: 'Die Ansichtskonfiguration wurde erfolgreich gespeichert.',
                icon: 'submit',
                iconSize: 24,
                iconStrokeWidth: 1
              }
            });

            return [
              UserViewConfigActions.createUserViewConfigSuccess({config: created}),
              UserViewConfigActions.navigateAfterViewConfigHandled({restartApp: restartAppAfterwards})
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map((mappedError: MappedHttpErrorResponse) => {
              this._logger.error('createUserViewConfig failed.', 'error:', mappedError);
              return UserViewConfigActions.createUserViewConfigFailure({error: mappedError});
            })
          ))
        )
      )
    )
  );


  readonly updateUserViewConfig$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserViewConfigActions.updateUserViewConfig),
      switchMap(({config}) =>
        this._viewConfigService.updateViewConfiguration(config).pipe(
          map((updated: ViewConfigurationDTO) => {
            this._logger.debug('updateUserViewConfig succeeded.');
            return UserViewConfigActions.updateUserViewConfigSuccess({config: updated});
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map((mappedError: MappedHttpErrorResponse) => {
              this._logger.error('updateUserViewConfig failed.', 'error:', mappedError);
              return UserViewConfigActions.updateUserViewConfigFailure({error: mappedError});
            }),
          )),
        )
      )
    )
  );

  readonly userViewConfigMutationFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(
        UserViewConfigActions.createUserViewConfigFailure,
        UserViewConfigActions.updateUserViewConfigFailure,
      ),
      map(({error}) => {
        let errorMsg = '';

        switch (error.status) {
          case 400:
            errorMsg = 'Die Anfrage war ungültig. Bitte überprüfe deine Eingaben oder versuche es später erneut.';
            break;
          case 403:
            errorMsg = 'Fehlende Berechtigung für die View-Konfiguration. Bitte kontaktiere deinen Administrator.';
            break;
          case 404:
            errorMsg = 'Die View-Konfiguration für deinen Benutzer wurde nicht gefunden.';
            break;
          case 503:
            errorMsg = 'Der Service zur View-Konfiguration ist derzeit nicht verfügbar. Bitte versuche es später erneut.';
            break;
          default:
            errorMsg = 'Fehler beim Speichern der View-Konfiguration. Bitte probiere es später erneut.';
        }

        this._snackbar.open(errorMsg, undefined, {
          duration: 5000,
          panelClass: 'error',
        });
      }),
    ),
    {dispatch: false}
  );

}
